// Reusable contacts_v2 picker. Ticket 188: it searches the server as you type
// (shared/useContactSearch) instead of loading the first 200 contacts and
// filtering in the browser, so any of the tenant's ~47k contacts can be picked.
import React, { useEffect, useState } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { uc } from '../unifiedContactsApi';
import { errorMessage } from '../errors';
import useContactSearch from './useContactSearch';
import { primaryEmail } from './ContactSearchInput';

const FALLBACK_NAME = 'Linked contact';

const ROW_CLS = 'w-full flex flex-col px-2 py-1.5 rounded-lg text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none';
const NOTE_CLS = 'px-2 py-3 text-center text-xs text-slate-500';

export default function ContactPicker({
  value,
  onChange,
  label = 'Borrower (Contact)',
  placeholder = 'Select a contact…',
  allowNone = true,
  testId,
  onCreateNew, // optional: renders a "+ New contact…" row that calls this instead of onChange (ticket 75)
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [known, setKnown] = useState(null); // the contact `value` points at: {contact_id, display_name}
  const { items, total, idle, loading, error } = useContactSearch(query, { enabled: open });
  const tid = (suffix) => (testId ? `${testId}-${suffix}` : undefined);

  // The linked contact is almost never in a result list, so show it by fetching it
  // by id. A pick already carries its record, so it is not fetched a second time.
  useEffect(() => {
    if (!value) { setKnown(null); return undefined; }
    if (known?.contact_id === value) return undefined;
    let live = true;
    uc.get(value)
      .then((c) => { if (live) setKnown({ contact_id: value, display_name: c?.display_name || FALLBACK_NAME }); })
      .catch(() => { if (live) setKnown({ contact_id: value, display_name: FALLBACK_NAME }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const changeOpen = (next) => { setOpen(next); if (!next) setQuery(''); };
  const choose = (id, contact) => {
    if (contact) setKnown({ contact_id: id, display_name: contact.display_name || FALLBACK_NAME });
    changeOpen(false);
    // A pick also hands over its record, for callers that fill fields from it (ticket 206).
    if (contact) onChange(id, contact); else onChange(id);
  };

  const name = value && known?.contact_id === value ? known.display_name : null;
  const shown = value ? (name || 'Loading…') : placeholder;
  const q = query.trim();

  return (
    <div>
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <Popover open={open} onOpenChange={changeOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            data-testid={testId}
            className={`flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring ${label ? 'mt-1' : ''}`}
          >
            <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>{shown}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-64 p-2">
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email or phone"
              autoComplete="off"
              className="h-8 pl-8 text-sm"
              data-testid={tid('search')}
            />
          </div>
          <div className="max-h-64 overflow-y-auto -mx-1">
            <div role="listbox" aria-label="Contacts">
              {onCreateNew && (
                <button type="button" role="option" aria-selected="false" data-testid={tid('new')}
                  onClick={() => { changeOpen(false); onCreateNew(); }}
                  className={`${ROW_CLS} font-medium`} style={{ color: '#070B5F' }}>
                  + New contact…
                </button>
              )}
              {allowNone && (
                <button type="button" role="option" aria-selected={!value} data-testid={tid('none')}
                  onClick={() => choose(null)} className={ROW_CLS}>
                  — None —
                </button>
              )}
              {items.map((c) => {
                const email = primaryEmail(c);
                return (
                  <button key={c.contact_id} type="button" role="option" aria-selected={c.contact_id === value}
                    data-testid={tid(`option-${c.contact_id}`)} onClick={() => choose(c.contact_id, c)}
                    className={`${ROW_CLS} ${c.contact_id === value ? 'bg-slate-50' : ''}`}>
                    <span className="truncate" style={{ color: '#070B5F' }}>{c.display_name || 'Unnamed'}</span>
                    {email && <span className="truncate text-xs text-slate-500">{email}</span>}
                  </button>
                );
              })}
            </div>
            {error ? (
              <div className={`${NOTE_CLS} text-red-600`} data-testid={tid('error')}>{errorMessage(error, 'Could not search contacts')}</div>
            ) : loading ? (
              <div className={`${NOTE_CLS} flex items-center justify-center gap-1.5`} data-testid={tid('loading')}>
                <Loader2 size={12} className="animate-spin" /> Searching…
              </div>
            ) : idle ? (
              <div className={NOTE_CLS} data-testid={tid('hint')}>Type to search</div>
            ) : items.length === 0 ? (
              <div className={NOTE_CLS} data-testid={tid('empty')}>No contacts match "{q}"</div>
            ) : total > items.length ? (
              <div className={NOTE_CLS} data-testid={tid('more')}>
                Showing {items.length.toLocaleString('en-US')} of {total.toLocaleString('en-US')}. Keep typing to narrow it down.
              </div>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
